import type { ReactNode } from "react";
import { getC1EloEquipmentIcon } from "../furniture/C1_EloEquipmentIcon";
import Group from "../../../src/models/Group";
import Room from "../../../src/models/Room";
import type { C1_DefinitionItem } from "./C1_DefinitionItem";
import { C1_BaseObject2 } from "./C1_BaseObject2";
import { C1_Furniture } from "./C1_Furniture";
import { C1_Opening } from "./C1_Opening";
import { C1_Room } from "./C1_Room";
import { C1_EloGroup } from "./C1_EloGroup";
import { materializeOptionalDefinitionChild } from "./C1_materializeEloDefinition";
import { commitOwnerEloTree, eloRootAndOwner } from "./C1_eloOwnerEditing";
import { appendRepeatedItem, repeatItemCount, repeatItemLimit } from "./C1_eloTreeEditing";
import { getRoomEloItemProfile, getEloItemProfileForInstance } from "../data/C1_eloItemsRegistry";
import { getOpeningEloProfile } from "../data/C1_OpeningEloDefinitions";
import type { C1_EloItem_Definition } from "../types/C1_EloTypes";
import type { C1_LegacyEloItemTreeData } from "../types/C1_EloTypes";
import { C1_EloItem_Type } from "../types/C1_EloTypes";
import { isSpecialExteriorRoomType } from "../types/C1_RoomTypes";

type DefinitionContainer = Room | Group;

type C1_DefinitionMenuItem = {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
};

type C1_DefinitionMenu = {
  title: string;
  items: C1_DefinitionMenuItem[];
  disabledItemIds: string[];
};

const EMPTY_MENU_ITEM_ID = "c1-no-additional-definitions";

function definitionMenuIcon(definition: C1_EloItem_Definition): ReactNode {
  return <span className="C1_navigatorMenuIcon">{getC1EloEquipmentIcon(definition, definition.defaultDetails)}</span>;
}

/** Finds the first node created by a tree edit without relying on generated IDs. */
function findNewTreeItemId(item: C1_LegacyEloItemTreeData, previousIds: Set<string>): string | undefined {
  if (!previousIds.has(item.id)) return item.id;
  for (const child of item.items) {
    const childId = findNewTreeItemId(child, previousIds);
    if (childId) return childId;
  }
  return undefined;
}

/** Adds one repeated child through the same canonical tree edit used by the plan view. */
function appendRepeatedNavigatorItem(group: C1_EloGroup, definition: C1_DefinitionItem): void {
  const context = eloRootAndOwner(group);
  if (!context) return;

  const previousIds = new Set<string>();
  for (const item of context.root.allItems) previousIds.add(item.id);
  const currentRoot = context.root.toLegacyTreeData();
  const nextRoot = appendRepeatedItem(
    currentRoot,
    group.id,
    definition.toDefinition(),
    state.c1.project.equipmentStandard,
  );
  if (nextRoot === currentRoot) return;

  const selectedItemId = findNewTreeItemId(nextRoot, previousIds);
  commitOwnerEloTree(context.owner, nextRoot, selectedItemId, false);
}

/** Adds the +1 action when the selected ELO group has remaining repeat capacity. */
function appendRepeatMenuItem(
  items: C1_DefinitionMenuItem[],
  container: DefinitionContainer,
  definition: C1_DefinitionItem | undefined,
): void {
  const group = container.c1;
  if (!(group instanceof C1_EloGroup) || !definition) return;

  const legacyDefinition = definition.toDefinition();
  const currentCount = repeatItemCount(legacyDefinition, group.toLegacyTreeData());
  if (currentCount >= repeatItemLimit(legacyDefinition)) return;

  items.push({
    id: `${definition.id}-append-repeat`,
    label: "+1 Steckdose",
    icon: definitionMenuIcon(legacyDefinition.items?.[0] ?? legacyDefinition),
    onClick: () => {
      state.undoManager.runAsSingleStep("Steckdose hinzufuegen", () => {
        appendRepeatedNavigatorItem(group, definition);
      });
    },
  });
}

/** Resolves the definition represented by a room or ESTADA group node. */
export function getC1ContainerDefinition(container: DefinitionContainer): C1_DefinitionItem | undefined {
  const facet = container.c1;
  if (facet instanceof C1_BaseObject2) return facet.definition;

  let rootDefinitionId = "";
  if (facet instanceof C1_Room) rootDefinitionId = facet.eloTree?.definitionId ?? "";
  else if (facet instanceof C1_Furniture) rootDefinitionId = facet.eloTree?.definitionId ?? "";
  else if (facet instanceof C1_Opening) rootDefinitionId = facet.eloTree?.definitionId ?? "";
  if (!rootDefinitionId) return undefined;
  return state.objectRegistry.getObject<C1_DefinitionItem>(rootDefinitionId);
}

/** Collects definition paths already represented by direct ESTADA children. */
function manifestedDefinitionPaths(container: DefinitionContainer): Set<string> {
  const paths = new Set<string>();
  const collections = [container.groups, container.consumers, container.buttons];
  for (const collection of collections) {
    for (const object of collection) {
      if (object.c1 instanceof C1_BaseObject2 && object.c1._defId) paths.add(object.c1._defId);
    }
  }
  return paths;
}

/** Uses the same instance-specific profiles as the floating ELO editor. */
export function getNavigatorEloDefinitions(container: DefinitionContainer): C1_EloItem_Definition[] | undefined {
  const facet = container.c1;
  if (facet instanceof C1_Room) return getRoomEloItemProfile(facet.type);
  if (facet instanceof C1_Opening) return getOpeningEloProfile(facet).definitions;
  if (facet instanceof C1_Furniture) return getEloItemProfileForInstance(facet.definitionId, facet.eloTree);
  const parent = container.parent as DefinitionContainer | undefined;
  if (facet instanceof C1_BaseObject2 && (parent?.isRoom || parent?.isGroup)) {
    const parentDefinitions = getNavigatorEloDefinitions(parent as DefinitionContainer);
    if (parentDefinitions) return parentDefinitions.find(definition => definition.id === facet.definition?.definitionId)?.items ?? [];
  }
  return undefined;
}

/** Returns optional direct children that have not yet been instantiated. */
export function getMissingOptionalDefinitions(
  container: DefinitionContainer,
  parentDefinition: C1_DefinitionItem | undefined,
): C1_DefinitionItem[] {
  if (!parentDefinition) return [];
  const manifestedPaths = manifestedDefinitionPaths(container);
  const configuredDefinitions = getNavigatorEloDefinitions(container);
  let furnitureOwner: DefinitionContainer | undefined = container;
  while (furnitureOwner && !(furnitureOwner.c1 instanceof C1_Furniture)) {
    furnitureOwner = furnitureOwner.parentGroup;
  }
  const wallDocked = furnitureOwner?.c1 instanceof C1_Furniture && furnitureOwner.c1.wallDocked !== undefined;
  const result: C1_DefinitionItem[] = [];
  for (const definition of parentDefinition.children) {
    const configured = configuredDefinitions?.find(item => item.id === definition.definitionId);
    if (configuredDefinitions && !configured) continue;
    const properties = configured ?? definition.definitionProperties;
    if (container.c1 instanceof C1_Room && isSpecialExteriorRoomType(container.c1.type)
      && definition.type === C1_EloItem_Type.IntermediateDistribution) continue;
    if (properties.allways || properties.requiresFurnitureProperty) continue;
    if (properties.onlyIfWallDocked && !wallDocked) continue;
    if (manifestedPaths.has(definition.id)) continue;
    result.push(definition);
  }
  return result;
}

/** Builds the navigator submenu that materializes optional C1 definitions. */
export function buildC1DefinitionAddMenu(
  container: DefinitionContainer,
  parentDefinition: C1_DefinitionItem | undefined,
): C1_DefinitionMenu {
  const definitions = getMissingOptionalDefinitions(container, parentDefinition);
  const items: C1_DefinitionMenuItem[] = [];
  const configuredDefinitions = getNavigatorEloDefinitions(container);
  for (const definition of definitions) {
    const configuredDefinition = configuredDefinitions?.find(item => item.id === definition.definitionId);
    items.push({
      id: definition.id,
      label: definition.description[state.c1.language] || definition.definitionId,
      icon: definitionMenuIcon(configuredDefinition ?? definition.toDefinition()),
      onClick: () => {
        const label = definition.description[state.c1.language] || definition.definitionId;
        state.undoManager.runAsSingleStep(`${label} hinzufügen`, () => {
          const created = materializeOptionalDefinitionChild(container, definition,
            configuredDefinition);
          if (created) state.selectionManager.selectedObject = created;
        });
      },
    });
  }
  appendRepeatMenuItem(items, container, parentDefinition);

  const disabledItemIds: string[] = [];
  if (items.length === 0) {
    items.push({
      id: EMPTY_MENU_ITEM_ID,
      label: "Es sind keine weiteren Unterobjekte konfiguriert",
      onClick: () => undefined,
    });
    disabledItemIds.push(EMPTY_MENU_ITEM_ID);
  }

  return { title: state.translate("roomTreeViewModel.add", "Hinzufügen"), items, disabledItemIds };
}
